import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import SupplierClassificationFields from '../../../components/SupplierClassificationFields';
import { useToast } from '../../../components/ui/Toast';
import { inviteService, getErrorMessage } from '../../../services/apiService';
import apiClient from '../../../services/apiService';
import { needsQualityGate, approverNames, classificationOf, isClassified } from '../../../utils/classification';

/**
 * Invite a vendor. Besides the name and e-mail, the form records what the supplier is (nature of
 * business, OEM/ODM, sub-category); that decides who has to approve the invite. The registration
 * link is e-mailed to the supplier only after that approval.
 */
const InviteVendorModal = ({ isOpen, onClose, onSuccess, prefillData, requestId }) => {
  const toast = useToast();
  const [name, setName] = useState(prefillData?.name || '');
  const [email, setEmail] = useState(prefillData?.email || '');
  // From an approved supplier request the plant's own answers arrive filled in.
  const [classification, setClassification] = useState(() => classificationOf(prefillData));
  const [emailError, setEmailError] = useState('');
  const [result, setResult] = useState(null); // the created invite (awaiting approval)
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const validateEmail = (value) => {
    if (!value) return '';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(value) ? '' : 'Invalid Email Id format';
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    setEmailError(validateEmail(value));
  };

  const classificationComplete = isClassified(classification);

  const handleCreateInvite = async () => {
    const emailValidation = validateEmail(email);
    if (emailValidation) {
      setEmailError(emailValidation);
      return;
    }
    if (!classificationComplete) {
      setError('Choose the nature of business, OEM/ODM and the supplier sub-category');
      return;
    }

    setLoading(true);
    setError('');
    setEmailError('');
    setResult(null);
    try {
      let data;
      if (requestId) {
        // Re-invite from an approved supplier request
        const response = await apiClient.post(`/supplier-requests/${requestId}/invite-vendor`, classification);
        data = response.data?.data?.invite || response.data?.invite || response.data;
      } else {
        const response = await inviteService.createInvite({ name, email, ...classification });
        data = response?.data || response;
      }

      setResult(data);
      if (data.email_sent === false) {
        setError(data.error_message || 'The approval request e-mail could not be sent');
        toast.warning('Invite created, but the approval request e-mail failed. The approver can still find it under Approvals › Invite Approval.');
      } else {
        toast.success(`Invite sent for approval to ${approverNames(data.approvers, 'the administrators')}`);
      }
      if (onSuccess) setTimeout(() => onSuccess(), 1500);
    } catch (e) {
      console.error('Failed to create invite:', e);
      const errorMsg = getErrorMessage(e);
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Support both controlled (isOpen) and uncontrolled modes
  if (isOpen === false) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-200" onClick={onClose} />
      <div className="fixed inset-0 z-200 flex items-center justify-center p-4">
        {/* No overflow-hidden here: the dropdown lists (nature of business, sub-category) open below
            their field and must be able to extend past the dialog's bottom edge. */}
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
          <div className="p-6 border-b border-gray-200 flex items-center justify-between">
            <h3 className="text-2xl font-bold text-gray-900">Invite Vendor</h3>
            <button
              onClick={onClose}
              className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors"
              aria-label="Close"
            >
              <Icon name="X" size={20} />
            </button>
          </div>
          <div className="p-6 space-y-4">
            {requestId && (
              <div className="mb-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
                <Icon name="Info" size={16} className="inline mr-2" />
                Inviting vendor from approved supplier request
              </div>
            )}
            <Input
              label="Vendor Name"
              type="text"
              placeholder="Acme Corp"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={!!requestId || !!result}
              required
            />
            <Input
              label="Vendor Email"
              type="email"
              placeholder="vendor@example.com"
              value={email}
              onChange={handleEmailChange}
              disabled={!!requestId || !!result}
              required
              error={emailError}
            />
            <SupplierClassificationFields
              value={classification}
              onChange={(field, code) => setClassification((current) => ({ ...current, [field]: code }))}
              disabled={!!result}
              idPrefix="invite"
            />
            {!result && (
              <p className="text-xs text-slate-500">
                The invite goes to the people set up for this combination in User Management › Invite approvers. The supplier
                receives the registration link once it is approved.
                {needsQualityGate(classification.supplier_subcategory) && ' RM and PM suppliers also wait for the quality department after they register.'}
              </p>
            )}
            {error && <div className="text-sm text-red-600">{error}</div>}
            {result && (
              <div className="p-4 rounded-lg bg-amber-50 border border-amber-200" data-testid="invite-pending-approval">
                <div className="flex items-center gap-2 mb-1">
                  <Icon name="Clock" size={18} className="text-amber-700" />
                  <span className="font-semibold text-amber-900">Sent for approval</span>
                </div>
                <p className="text-sm text-amber-900">
                  {(result.approvers || []).length
                    ? <>The invite is waiting for <strong>{approverNames(result.approvers)}</strong>{result.approvers.length > 1 ? ' (any one of them can approve)' : ` (${result.approvers[0].email})`}.</>
                    : <>No approver is configured for this combination, so the invite is waiting for an administrator.</>}
                  {' '}The registration link will be e-mailed to <strong>{email}</strong> once it is approved, and you will be told the decision.
                </p>
              </div>
            )}
          </div>
          <div className="p-6 border-t border-gray-200 flex items-center justify-end gap-3">
            <button
              onClick={onClose}
              className="px-5 py-2.5 border-2 border-slate-300 text-slate-700 rounded-lg font-semibold bg-slate-50 hover:bg-slate-100 transition-colors"
            >
              Close
            </button>
            {!result && (
              <button
                onClick={handleCreateInvite}
                disabled={loading || !email || !name || !!emailError || !classificationComplete}
                className="px-5 py-2.5 bg-blue-600 text-white rounded-lg font-bold disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 hover:bg-blue-700 transition-colors"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Sending...
                  </>
                ) : (
                  <span>Send for Approval</span>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default InviteVendorModal;
